import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { portalApi, getStoredUser, setSession } from "../../lib/portalApi";

const ADMIN_TOKEN_KEY = "tf_portal_admin_token";
const ADMIN_USER_KEY = "tf_portal_admin_user";

const ROLE_OPTIONS = [
  { value: "client", label: "Client", hint: "External view · Financials read-only" },
  { value: "client_project_manager", label: "Client Project Manager", hint: "Client-side PM · Financials read-only" },
  { value: "client_interior_designer", label: "Client Interior Designer", hint: "Client-side designer · no financials" },
  { value: "project_manager", label: "Project Manager", hint: "Internal PM · no financials" },
  { value: "site_engineer", label: "Site Engineer", hint: "Mobile-first site view · no financials" },
  { value: "supplier", label: "Supplier / Vendor", hint: "External supply view · no financials" },
];

const ROLE_LABEL = {
  client: "Client",
  client_project_manager: "Client Project Manager",
  client_interior_designer: "Client Interior Designer",
  project_manager: "Project Manager",
  site_engineer: "Site Engineer",
  admin_staff: "Admin Staff",
  consultant: "Consultant",
  subcontractor: "Subcontractor",
  supplier: "Supplier / Vendor",
};

/**
 * Dropdown button for Main Admin to enter "Preview as <role>" mode for a
 * specific project. Saves the current admin token aside and swaps the
 * frontend session to a short-lived impersonation token.
 */
export function PreviewAsButton({ slug, projectName }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const start = async (role) => {
    setBusy(true); setErr("");
    try {
      // The backend swaps the httpOnly session cookie to a short-lived
      // impersonation token; we only cache the user object for instant paint.
      const res = await portalApi.impersonate(role, slug);
      setSession(res.token, {
        ...(getStoredUser() || {}),
        role,
        impersonating: true,
        impersonate_role: role,
        impersonate_project_slug: slug,
      });
      setOpen(false);
      // Hard reload so all in-memory caches reset to the impersonated view.
      window.location.href = `/projects/${slug}`;
    } catch (e) {
      setErr(e.message || "Could not start preview.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="portal-preview-as-btn"
        className="inline-flex items-center gap-2 border border-[#D4A963] text-[#6E1F2C] bg-[#FAF5EC] hover:bg-[#D4A963]/20 px-4 py-2.5 text-[10px] tracking-[0.26em] uppercase transition-colors"
      >
        <Eye size={13} /> Preview as…
      </button>
      {open && (
        <div
          data-testid="portal-preview-as-menu"
          className="absolute right-0 mt-2 w-64 bg-white border border-[#6E1F2C]/25 shadow-[0_18px_40px_-15px_rgba(110,31,44,0.35)] z-40"
        >
          <div className="px-4 py-3 border-b border-[#6E1F2C]/10">
            <p className="text-[9px] tracking-[0.32em] uppercase text-[#7A6B5F]">Preview project</p>
            <p className="text-sm text-[#1C110C] font-serif-display leading-tight line-clamp-1">{projectName || slug}</p>
          </div>
          <ul className="divide-y divide-[#6E1F2C]/10">
            {ROLE_OPTIONS.map((r) => (
              <li key={r.value}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => start(r.value)}
                  data-testid={`portal-preview-as-${r.value}`}
                  className="w-full text-left px-4 py-3 hover:bg-[#FAF5EC] transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  <p className="text-sm text-[#1C110C] font-medium">{r.label}</p>
                  <p className="text-[10px] text-[#7A6B5F] tracking-wide">{r.hint}</p>
                </button>
              </li>
            ))}
          </ul>
          {busy && (
            <div className="px-4 py-3 flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F] border-t border-[#6E1F2C]/10">
              <Loader2 size={12} className="animate-spin" /> Starting preview…
            </div>
          )}
          {err && (
            <p className="px-4 py-3 text-[11px] text-[#A3141A] border-t border-[#6E1F2C]/10">{err}</p>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full px-4 py-2.5 text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C] border-t border-[#6E1F2C]/10"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Sticky banner shown at the top of PortalLayout while a Main Admin is in
 * "Preview as <role>" mode. Provides the only escape hatch back to admin.
 */
export function PreviewModeBanner({ user }) {
  const [busy, setBusy] = useState(false);
  const exit = async () => {
    setBusy(true);
    try {
      // The backend restores the full-admin session cookie.
      try {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_USER_KEY);
      } catch { /* ignore */ }
      const res = await portalApi.endImpersonation();
      setSession(res.token, res.user);
      window.location.href = "/projects/admin";
    } finally {
      setBusy(false);
    }
  };
  const role = user?.impersonate_role || user?.role;
  const label = ROLE_LABEL[role] || role;
  const projectSlug = user?.impersonate_project_slug;
  return (
    <div
      data-testid="portal-preview-banner"
      className="bg-gradient-to-r from-[#D4A963] via-[#C8A75D] to-[#A6822F] text-[#1C110C] border-b border-[#6E1F2C]/30"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Eye size={14} className="shrink-0" />
          <p className="text-[11px] tracking-[0.22em] uppercase font-medium truncate">
            Preview Mode · You are viewing the portal as
            <span className="ml-1.5 inline-block bg-[#6E1F2C] text-[#FAF5EC] px-2 py-0.5 tracking-[0.2em]">
              {label}
            </span>
            {projectSlug && (
              <span className="ml-2 normal-case tracking-[0.16em] text-[#3E2A22] text-[10px]" data-testid="portal-preview-banner-project">
                · {projectSlug}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={exit}
          disabled={busy}
          data-testid="portal-preview-exit"
          className="inline-flex items-center gap-2 bg-[#1C110C] text-[#FAF5EC] hover:bg-[#3E2A22] px-3 py-1.5 text-[10px] tracking-[0.26em] uppercase transition-colors disabled:opacity-60"
        >
          <EyeOff size={12} /> {busy ? "Exiting…" : "Exit Preview"}
        </button>
      </div>
    </div>
  );
}
